import Point from "./Point.js";
import Coord from "./Coord.js";
import { Bezier } from "bezier-js";

/**
 * A shape defined in terms of curve points
 */
class PointCurveShape {
	points: Point[];

  constructor() {
    this.points = [];
  }
  current() {
    return this.points.slice(-1)[0];
  }
  addPoint(x: number, y: number) {
    this.points.push(new Point(x, y));
  }
  setLeft(x: number, y: number) {
    this.current().setLeft(x, y);
  }
  setRight(x: number, y: number) {
    this.current().setRight(x, y);
  }
  close() {
    const points = this.points;
    if (points.length > 0) {
      const first = points[0];
      const last = points[points.length - 1];

      if (first.isEqual(last)) {
        const main = first.main;
        const right = first.right || last.right;
        const left = first.left || last.left;
        
        first.main = main;
        first.left = left;
        first.right = right;

        points.pop(); // remove the last point if it is equal to the first
      }
    }
  }
  insertPoint(index: number, x: number, y: number) {
    if (index < 0 || index > this.points.length) {
      throw new Error("Index out of bounds");
    }
    const newPoint = new Point(x, y);
    if (index === this.points.length) {
      this.points.push(newPoint);
    } else {
      this.points.splice(index, 0, newPoint);
    }
  }

  get segments() {
    const segments: ({
      type: "line";
      start: Coord;
      end: Coord;
    } | {
      type: "bezier";
      start: Coord;
      end: Coord;
      control1?: Coord;
      control2?: Coord;
    })[] = [];

    const points = this.points;
    if (points.length < 2) {
      return segments; // not enough points to form a segment
    }
    for (let i = 0; i < points.length; i++) {
      const current = points[i];
      const next = points[(i + 1) % points.length]; // wrap around to the first point
      if (current.isPlain && next.isPlain) {
        // both points are plain, create a line segment
        segments.push({
          type: "line",
          start: current.getPoint(),
          end: next.getPoint(),
        });
      } else {
        // at least one point is not plain, create a bezier segment
        segments.push({
          type: "bezier",
          start: current.getPoint(),
          end: next.getPoint(),
          control1: current.getRight(),
          control2: next.getLeft(),
        });
      }
    }

    return segments;
  }

  getClosestPoint(x: number, y: number) {
    const segments = this.segments;
    console.log("Segments:", segments);
    let closestPoint = null;
    let closestDistance = Infinity;
    let index = -1;
    let i = -1;
    for (const segment of segments) {
      i++;
      if (segment.type === "bezier") {
        const bezier = new Bezier(
          segment.start.x,
          segment.start.y,
          segment.control1?.x ?? segment.start.x,
          segment.control1?.y ?? segment.start.y,
          segment.control2?.x ?? segment.end.x,
          segment.control2?.y ?? segment.end.y,
          segment.end.x,
          segment.end.y
        );
        const pointOnSegment = bezier.project({ x, y });
        const distance = Math.sqrt(
          Math.pow(pointOnSegment.x - x, 2) +
          Math.pow(pointOnSegment.y - y, 2)
        );
        if (distance < closestDistance) {
          closestDistance = distance;
          closestPoint = new Coord(pointOnSegment.x, pointOnSegment.y);
          index = i + 1;
        }
      }
      else if (segment.type === "line") {
        const start = segment.start;
        const end = segment.end;
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const lengthSquared = dx * dx + dy * dy;

        // Find the projection of the point onto the line segment
        let t = ((x - start.x) * dx + (y - start.y) * dy) / lengthSquared;
        t = Math.max(0, Math.min(1, t)); // clamp t to [0, 1]

        const closestX = start.x + t * dx;
        const closestY = start.y + t * dy;
        const distance = Math.sqrt(
          Math.pow(closestX - x, 2) + Math.pow(closestY - y, 2)
        );

        if (distance < closestDistance) {
          closestDistance = distance;
          closestPoint = new Coord(closestX, closestY);
          index = i + 1;
        }
      }
    }
    
    return {
      point: closestPoint,
      index: index,
    };
  }

  splitAtPoint(x: number, y: number) {
    const closest = this.getClosestPoint(x, y);
    if (closest.point) {
      const index = closest.index;
      const point = closest.point;
      const segment = this.segments[index - 1];
      if (segment.type == "line") {
        // if the segment is a line, we can just insert the point
        this.insertPoint(index, point.x, point.y);
      }
      else if (segment.type == "bezier") {
        // if the segment is a bezier, we need to split it
        const bezier = new Bezier(
          segment.start.x,
          segment.start.y,
          segment.control1?.x ?? segment.start.x,
          segment.control1?.y ?? segment.start.y,
          segment.control2?.x ?? segment.end.x,
          segment.control2?.y ?? segment.end.y,
          segment.end.x,
          segment.end.y
        );
        // Find the t value for the closest point
        const t = bezier.project({ x, y }).t;
        if (t == null) {
          console.warn("Could not find t value for point:", point);
          return null;
        }
        // Split the bezier curve at t
        const split = bezier.split(t);
        if (split && split.left && split.right) {
          // Create new points from the split segments
          const leftPoints = split.left.points;
          const rightPoints = split.right.points;

          const a = this.points[index - 1];
          const c = this.points[index % this.points.length];
          a.setRight(leftPoints[1].x, leftPoints[1].y);
          c.setLeft(rightPoints[rightPoints.length - 2].x, rightPoints[rightPoints.length - 2].y);

          // Add the center point of the split
          const b = new Point(rightPoints[0].x, rightPoints[0].y);
          b.setLeft(leftPoints[leftPoints.length - 2].x, leftPoints[leftPoints.length - 2].y);
          b.setRight(rightPoints[1].x, rightPoints[1].y);

          console.log("Split bezier curve at t:", t, "Left points:", leftPoints, "Right points:", rightPoints);

          // Insert the new point in the middle of the split
          this.points.splice(index, 0, b);
        }
      }
    }
    return null;
  }

  /**
   * Convert the point curve to an SVG path string
   * (bidirectional conversion? You better believe it).
   * This code is very similar to the draw method,
   * since it effectively does the same thing.
   */
  toSVG() {
    // first vertex
    let points = this.points;
    let first = points[0].getPoint();
    let x = first.x;
    let y = first.y;
    let svg = "M" + x + (y < 0 ? y : " " + y);
    // rest of the shape
    for (let p = 1; p <= points.length; p++) {
      const i = p % points.length; // wrap around
      // since we have to work with point pairs, get "prev" and "current"
      let prev = points[p - 1];
      let curr = points[i];
      // if both are plain, LineTo. Otherwise, Cubic bezier.
      if (curr.isPlain && prev.isPlain) {
        let lx = curr.getPoint().x;
        let ly = curr.getPoint().y;
        svg += "L" + lx + (ly < 0 ? ly : " " + ly);
      } else {
        let cx1 = prev.getRight().x;
        let cy1 = prev.getRight().y;
        let cx2 = curr.getLeft().x;
        let cy2 = curr.getLeft().y;
        let x2 = curr.getPoint().x;
        let y2 = curr.getPoint().y;
        svg +=
          "C" +
          cx1 +
          (cy1 < 0 ? cy1 : " " + cy1) +
          (cx2 < 0 ? cx2 : " " + cx2) +
          (cy2 < 0 ? cy2 : " " + cy2) +
          (x2 < 0 ? x2 : " " + x2) +
          (y2 < 0 ? y2 : " " + y2);
      }
    }
    return svg + "Z";
  }
}

export default PointCurveShape;
